import { useState } from "react";
import { BadgeCheck, Wrench } from "lucide-react";

import api from "../../lib/api";
import {
  PARTNER_PRICING,
  pricingEnquirySummary,
} from "../../config/landingPricing";
import { LaunchPricingExperience } from "./LaunchPricingExperience";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";

const PARTNER_CARDS = [
  {
    key: "vendor",
    title: "Grow your society business",
    eyebrow: "SOCIETY VENDORS",
    price: PARTNER_PRICING.vendor.price,
    badge: PARTNER_PRICING.vendor.badge,
    accent: "orange",
    details: [
      "Annual membership payable after approval.",
      "Business profile and relevant society requirements.",
      "Submit quotations for suitable projects.",
      "5% standard success fee on eligible platform-sourced work.",
      "Special-project commercial terms agreed separately.",
      "No guaranteed leads, contracts or earnings.",
    ],
    cta: "Apply as a Vendor",
  },
  {
    key: "technician",
    title: "Get work in your local area",
    eyebrow: "SERVICE TECHNICIANS",
    price: PARTNER_PRICING.technician.price,
    secondary: PARTNER_PRICING.technician.launchNote,
    accent: "blue",
    details: [
      "Initial pilot: Electrical and Plumbing in Boisar.",
      "20% commission on net visiting and labour charges.",
      "Separately itemized materials and taxes excluded from the commission base.",
      "Proposed Electrical/Plumbing diagnosis fee: ₹150, adjusted against approved labour if work proceeds.",
      "Verification and Super Admin approval required.",
    ],
    cta: "Register Your Interest",
  },
  {
    key: "expert",
    title: "Connect your expertise with societies",
    eyebrow: "PROFESSIONAL EXPERTS",
    price: PARTNER_PRICING.expert.price,
    secondary: PARTNER_PRICING.expert.launchNote,
    accent: "slate",
    details: [
      "Professional verification required.",
      "Experts set their consultation fees.",
      "Platform commercial terms agreed separately by profession.",
      "Paid consultation checkout is not active at launch.",
    ],
    cta: "Apply as an Expert",
  },
];

export default function PricingSection({ onExpert, onTechnician, onVendor }) {
  const [enquiryQuote, setEnquiryQuote] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const openEnquiry = (quote) => {
    setEnquiryQuote(quote);
    setEnquiryOpen(true);
  };

  return (
    <section className="scroll-mt-24 bg-slate-100 py-20 sm:py-28" id="pricing">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.16em] text-orange-600">PRICING</p>
          <h2 className="mt-4 font-display text-4xl font-black text-blue-950 sm:text-5xl">
            Simple pricing. Better society management.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Choose the right plan for your society or join our network of service professionals.
          </p>
        </div>

        <LaunchPricingExperience onRequestEnquiry={openEnquiry} />

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PARTNER_CARDS.map((card) => (
            <PartnerCard
              card={card}
              key={card.key}
              onClick={card.key === "vendor" ? onVendor : card.key === "expert" ? onExpert : onTechnician}
            />
          ))}
        </div>
      </div>

      <PricingEnquiryDialog
          isCustom={enquiryQuote?.status === "custom_quote"}
        open={enquiryOpen}
        onOpenChange={setEnquiryOpen}
          summary={pricingEnquirySummary(enquiryQuote)}
      />
    </section>
  );
}

function PartnerCard({ card, onClick }) {
  const accent = {
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    orange: "border-orange-200 bg-orange-50 text-orange-800",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  }[card.accent];
  const button = {
    blue: "bg-blue-900 text-white hover:bg-blue-800",
    orange: "bg-orange-500 text-white hover:bg-orange-600",
    slate: "bg-slate-900 text-white hover:bg-slate-800",
  }[card.accent];
  return (
    <article className="flex h-full flex-col border border-slate-200 bg-white p-6 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className={`border px-2.5 py-1 text-xs font-bold tracking-[0.12em] ${accent}`}>
          {card.eyebrow}
        </span>
        {card.badge && <span className="bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">{card.badge}</span>}
      </div>
      <h3 className="mt-5 font-display text-2xl font-black text-blue-950">{card.title}</h3>
      <p className="mt-4 font-display text-3xl font-black text-blue-950">{card.price}</p>
      {card.secondary && <p className="mt-1 text-sm font-semibold text-slate-600">{card.secondary}</p>}
      {card.key === "vendor" && (
        <p className="mt-3 border-l-2 border-orange-400 pl-3 text-sm font-bold text-orange-800">
          {PARTNER_PRICING.vendor.successFee} on eligible platform-sourced work.
        </p>
      )}
      {card.key === "technician" && (
        <p className="mt-3 border-l-2 border-blue-500 pl-3 text-sm font-bold text-blue-800">
          Proposed terms — commercial agreement remains DRAFT until activated.
        </p>
      )}
      <ul className="mt-6 flex-1 space-y-3 text-sm leading-relaxed text-slate-700">
        {card.details.map((detail) => (
          <li className="flex gap-2" key={detail}>
            <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>{detail}</span>
          </li>
        ))}
      </ul>
      <Button
        className={`mt-7 h-11 w-full ${button}`}
        data-testid={`pricing-${card.key}-cta`}
        onClick={onClick}
        type="button"
      >
        {card.cta} <Wrench className="ml-2 h-4 w-4" />
      </Button>
    </article>
  );
}

function PricingEnquiryDialog({ isCustom, onOpenChange, open, summary }) {
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async () => {
    if (!form.society_name?.trim() || !form.contact?.trim() || !form.mobile?.trim()) {
      toast.error("Society name, your name and mobile number are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/leads", {
        ...form,
        message: summary,
        requirements: summary,
        type: isCustom ? "society_custom_quote" : "society_pricing_enquiry",
      });
      toast.success("Enquiry saved. Our team will contact you shortly.");
      setForm({});
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent data-testid="pricing-enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isCustom ? "Request a custom quotation" : "Request a society demo"}
          </DialogTitle>
          <DialogDescription>
            This saves an enquiry for our team. It does not start a subscription or collect payment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="border border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-blue-900" data-testid="pricing-enquiry-summary">
            {summary}
          </p>
          <div>
            <Label htmlFor="pricing-enquiry-society">Society name *</Label>
            <Input data-testid="pricing-enquiry-society" id="pricing-enquiry-society" onChange={(event) => update("society_name", event.target.value)} value={form.society_name || ""} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="pricing-enquiry-name">Your name *</Label>
              <Input data-testid="pricing-enquiry-name" id="pricing-enquiry-name" onChange={(event) => update("contact", event.target.value)} value={form.contact || ""} />
            </div>
            <div>
              <Label htmlFor="pricing-enquiry-mobile">Mobile *</Label>
              <Input data-testid="pricing-enquiry-mobile" id="pricing-enquiry-mobile" inputMode="tel" onChange={(event) => update("mobile", event.target.value)} value={form.mobile || ""} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="pricing-enquiry-email">Email</Label>
              <Input data-testid="pricing-enquiry-email" id="pricing-enquiry-email" onChange={(event) => update("email", event.target.value)} type="email" value={form.email || ""} />
            </div>
            <div>
              <Label htmlFor="pricing-enquiry-city">City</Label>
              <Input data-testid="pricing-enquiry-city" id="pricing-enquiry-city" onChange={(event) => update("city", event.target.value)} value={form.city || ""} />
            </div>
          </div>
          <Button data-testid="pricing-enquiry-submit" disabled={sending} onClick={submit} type="button">
            {sending ? "Saving enquiry…" : "Save enquiry"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}